import { Tooltip, Typography } from "@mui/material";
import { ReactNode, useEffect, useRef, useState } from "react";
import { myPrimaryColor } from "../../constants/colors";

interface ConditionalTooltipProps {
  children: ReactNode;
  tooltipText: string;
  tooltipStyles: {
    tooltip: Record<string, any>;
  };
  maxWidth: number;
}

// Component to conditionally show tooltip based on overflow
export const ConditionalTooltip = ({ children, tooltipText, tooltipStyles, maxWidth }: ConditionalTooltipProps) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkOverflow = () => {
      const element = textRef.current;
      if (element) {
        // Check if the element's content is wider than its visible width
        const isOverflowing = element.scrollWidth > element.clientWidth;
        setShowTooltip(isOverflowing);
      }
    };

    checkOverflow();

    // Use ResizeObserver to check when the element size changes
    const resizeObserver = new ResizeObserver(checkOverflow);
    if (textRef.current) {
      resizeObserver.observe(textRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [tooltipText]); // Re-run when text changes

  return (
    <Tooltip
      title={showTooltip ? tooltipText : ''}
      placement="bottom"
      enterDelay={500}
      leaveDelay={200}
      disableHoverListener={!showTooltip} // Disable tooltip when not overflowing
      slotProps={{
        tooltip: {
          sx: tooltipStyles.tooltip
        }
      }}
    >
      <Typography
        ref={textRef}
        variant={'h6'}
        sx={{
          fontWeight: 700,
          fontSize: '12px',
          color: myPrimaryColor,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          flexShrink: 1,
          flexGrow: 0,
          minWidth: 0,
          maxWidth: `${Math.max(0, (maxWidth / 2) - 10)}px`,
        }}
      >
        {children}
      </Typography>
    </Tooltip>
  );
};
